import type { BrowserOptions, Breadcrumb, ErrorEvent } from '@sentry/react';

type TransactionEvent = Parameters<
  NonNullable<BrowserOptions['beforeSendTransaction']>
>[0];

const REDACTED = '[redacted]';
const REDACTED_NIN = '[redacted-nin]';
const REDACTED_EMAIL = '[redacted-email]';
const UNSERIALIZABLE = '[unserializable]';

// 10- or 12-digit personnummer, optionally with a `-`/`+` separator.
const NIN_PATTERN = /\b\d{6,8}[-+]?\d{4}\b/g;
const EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
// The patient-form one-time code and the BankID autostart token travel in URLs.
const SECRET_PARAM_PATTERN =
  /([?&#](?:code|token|autostarttoken|access_token|refresh_token)=)[^&#\s"']*/gi;
const SENSITIVE_KEYS = new Set([
  'authorization',
  'cookie',
  'token',
  'accesstoken',
  'refreshtoken',
  'password',
  'personalnumber',
  'personnummer',
  'email',
  'phone',
  'phonenumber',
  'autostarttoken',
  'firstname',
  'lastname',
  'address',
]);
const URL_KEYS = ['url', 'from', 'to', 'http.url'];

export const redactString = (value: string): string =>
  value
    .replace(SECRET_PARAM_PATTERN, `$1${REDACTED}`)
    .replace(NIN_PATTERN, REDACTED_NIN)
    .replace(EMAIL_PATTERN, REDACTED_EMAIL);

export const stripQuery = (url: string): string => url.split(/[?#]/)[0];

const redactData = (data: unknown): unknown => {
  try {
    return JSON.parse(
      JSON.stringify(data, (key, value) => {
        if (value == null) return value;
        if (SENSITIVE_KEYS.has(key.toLowerCase())) return REDACTED;
        if (typeof value !== 'string') return value;
        return redactString(value);
      })
    );
  } catch {
    return UNSERIALIZABLE;
  }
};

const redactRecord = <T extends Record<string, unknown>>(record: T): T => {
  const scrubbed = redactData(record);
  if (scrubbed && typeof scrubbed === 'object') return scrubbed as T;
  return {} as T;
};

const scrubUrlFields = (
  data: Record<string, unknown>
): Record<string, unknown> => {
  const scrubbed = redactRecord(data);
  URL_KEYS.forEach((key) => {
    if (typeof scrubbed[key] === 'string') {
      scrubbed[key] = stripQuery(scrubbed[key] as string);
    }
  });
  delete scrubbed['http.query'];
  delete scrubbed['http.fragment'];
  return scrubbed;
};

const scrubRequest = (event: ErrorEvent | TransactionEvent) => {
  if (!event.request) return;
  if (typeof event.request.url === 'string') {
    event.request.url = stripQuery(event.request.url);
  }
  delete event.request.query_string;
  delete event.request.data;
  delete event.request.cookies;
  if (event.request.headers) {
    event.request.headers = redactRecord(event.request.headers);
  }
};

export const scrubBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb => {
  if (typeof breadcrumb.message === 'string') {
    breadcrumb.message = redactString(breadcrumb.message);
  }
  if (breadcrumb.data) breadcrumb.data = scrubUrlFields(breadcrumb.data);
  return breadcrumb;
};

export const scrubEvent = (event: ErrorEvent): ErrorEvent | null => {
  if (event.exception?.values?.some((ex) => ex.type === 'ExpectedError')) {
    return null;
  }
  event.exception?.values?.forEach((ex) => {
    if (ex.value) ex.value = redactString(ex.value);
  });
  if (typeof event.message === 'string') {
    event.message = redactString(event.message);
  }
  scrubRequest(event);
  if (event.extra) event.extra = redactRecord(event.extra);
  if (event.contexts) event.contexts = redactRecord(event.contexts);
  if (event.tags) event.tags = redactRecord(event.tags);
  event.breadcrumbs = event.breadcrumbs?.map(scrubBreadcrumb);
  return event;
};

export const scrubTransaction = (event: TransactionEvent): TransactionEvent => {
  if (typeof event.transaction === 'string') {
    event.transaction = stripQuery(redactString(event.transaction));
  }
  scrubRequest(event);
  event.spans?.forEach((span) => {
    if (typeof span.description === 'string') {
      span.description = stripQuery(redactString(span.description));
    }
    if (span.data) {
      span.data = scrubUrlFields(span.data) as typeof span.data;
    }
  });
  const traceData = event.contexts?.trace?.data;
  if (traceData && event.contexts?.trace) {
    event.contexts.trace.data = scrubUrlFields(traceData);
  }
  event.breadcrumbs = event.breadcrumbs?.map(scrubBreadcrumb);
  return event;
};
